<template>
    <div class="publications-review-archive space-y-5">
        <publications-nav :profile="profile" :dashboard="dashboard" />

        <section class="publication-controls" aria-label="Пошук і впорядкування публікацій">
            <label class="sr-only" for="publication-review-search">Пошук публікацій</label>
            <input
                id="publication-review-search"
                v-model="searchQuery"
                type="search"
                maxlength="200"
                class="foxy-input w-full !px-4 !py-2.5 text-sm"
                placeholder="Пошук за книгою, автором або текстом..."
                @input="scheduleReload"
                @keydown.enter.prevent="reloadArchive"
            />

            <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="scrollbar-hide flex min-w-0 gap-1 overflow-x-auto" role="group" aria-label="Тип публікації">
                    <button
                        v-for="option in typeOptions"
                        :key="option.value"
                        type="button"
                        class="publication-filter-chip"
                        :class="{ 'is-active': typeFilter === option.value }"
                        :aria-pressed="typeFilter === option.value ? 'true' : 'false'"
                        @click="setType(option.value)"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <label class="relative shrink-0">
                    <span class="sr-only">Порядок публікацій</span>
                    <select v-model="sortOrder" class="foxy-select !py-2 !pl-3 !pr-9 text-sm" @change="reloadArchive">
                        <option value="newest">Новіші</option>
                        <option value="oldest">Старіші</option>
                    </select>
                </label>
            </div>

            <p v-if="filtersActive && !archiveLoading" class="mt-2 text-xs text-foxy-text-secondary">Знайдено: {{ meta.total }}</p>
        </section>

        <div v-if="archiveLoading && !reviews.length" class="space-y-3" aria-live="polite">
            <div v-for="index in 3" :key="index" class="publication-card animate-pulse">
                <div class="h-24 w-16 shrink-0 rounded-lg bg-foxy-elevated"></div>
                <div class="min-w-0 flex-1 space-y-3 py-1">
                    <div class="h-4 w-2/5 rounded bg-foxy-elevated"></div>
                    <div class="h-3 w-1/4 rounded bg-foxy-elevated"></div>
                    <div class="h-3 w-full rounded bg-foxy-elevated"></div>
                    <div class="h-3 w-4/5 rounded bg-foxy-elevated"></div>
                </div>
            </div>
        </div>

        <div v-else-if="archiveError && !reviews.length" class="publication-state border-red-400/35 text-red-700 dark:text-red-200">
            {{ archiveError }}
            <button type="button" class="ml-2 font-semibold underline" @click="reloadArchive">Спробувати ще раз</button>
        </div>

        <div v-else-if="reviews.length" class="space-y-3">
            <article v-for="item in reviews" :key="item.id" class="publication-card">
                <a
                    v-if="item.book && item.book.url"
                    :href="item.book.url"
                    class="book-cover-frame block h-24 w-16 shrink-0 overflow-hidden rounded-lg border border-foxy-border sm:h-[7.5rem] sm:w-20"
                    tabindex="-1"
                    aria-hidden="true"
                >
                    <img
                        :src="resolveBookCover(item.book.cover)"
                        class="book-cover-media"
                        alt=""
                        loading="lazy"
                        width="80"
                        height="120"
                        @error="onCoverError"
                    />
                </a>
                <div v-else class="book-cover-frame h-24 w-16 shrink-0 overflow-hidden rounded-lg border border-foxy-border sm:h-[7.5rem] sm:w-20">
                    <img :src="resolveBookCover(null)" class="book-cover-media" alt="" @error="onCoverError" />
                </div>

                <div class="min-w-0 flex-1">
                    <header class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div class="min-w-0">
                            <a
                                v-if="item.book && item.book.url"
                                :href="item.book.url"
                                class="block text-base font-extrabold leading-snug text-foxy-text transition-colors hover:text-foxy-action sm:text-lg"
                            >
                                {{ item.book.title }}
                            </a>
                            <div v-else class="text-base font-extrabold leading-snug text-foxy-text sm:text-lg">
                                {{ item.book ? item.book.title : 'Книга недоступна' }}
                            </div>

                            <div v-if="item.book && item.book.authors && item.book.authors.length" class="mt-0.5 text-sm text-foxy-text-secondary">
                                <template v-for="(author, index) in item.book.authors">
                                    <span v-if="index" :key="`separator-${index}`">, </span>
                                    <a
                                        v-if="author.url"
                                        :key="author.url"
                                        :href="author.url"
                                        class="transition-colors hover:text-foxy-action hover:underline"
                                        >{{ author.name }}</a
                                    >
                                    <span v-else :key="`author-${index}`">{{ author.name }}</span>
                                </template>
                            </div>
                            <div v-else-if="item.book && item.book.author" class="mt-0.5 text-sm text-foxy-text-secondary">
                                {{ item.book.author }}
                            </div>
                        </div>

                        <div class="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                            <div v-if="item.rating !== null && item.rating !== undefined" class="flex items-center gap-1 font-bold text-foxy-text">
                                <i class="fas fa-star text-foxy-honey" aria-hidden="true"></i>
                                <span>{{ item.rating }}/10</span>
                            </div>
                            <span
                                v-if="moodPresentation(item.mood)"
                                :class="['publication-mood', moodPresentation(item.mood).classes]"
                                :data-review-mood="item.mood"
                            >
                                <span :class="['h-1.5 w-1.5 rounded-full', moodPresentation(item.mood).dotClass]" aria-hidden="true"></span>
                                {{ moodPresentation(item.mood).label }}
                            </span>
                        </div>
                    </header>

                    <div class="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-medium text-foxy-text-secondary">
                        <span>{{ typeLabel(item.review_type) }}</span>
                        <template v-if="formatLabel(item)">
                            <span aria-hidden="true">·</span>
                            <span>{{ formatLabel(item) }}</span>
                        </template>
                        <span aria-hidden="true">·</span>
                        <a :href="item.url" class="transition-colors hover:text-foxy-action">
                            <time :datetime="item.created_at">{{ formatDate(item.created_at) }}</time>
                        </a>
                    </div>

                    <div
                        v-if="item.status === 'blocked'"
                        class="mt-3 rounded-lg border border-red-400/45 bg-red-50/80 px-3 py-2 text-sm text-red-900 dark:bg-red-500/10 dark:text-red-100"
                    >
                        <div class="font-semibold">Контент заблоковано адміністрацією сайту</div>
                        <div v-if="isOwner && item.moderation_reason" class="mt-1 text-xs">Причина: {{ item.moderation_reason }}</div>
                    </div>

                    <div
                        v-else-if="isSpoilerProtected(item)"
                        class="relative mt-3 min-h-[6.5rem] overflow-hidden rounded-lg"
                        :data-review-spoiler="item.id"
                    >
                        <div
                            class="publication-preview publication-preview-clamped pointer-events-none select-none blur-sm"
                            v-html="bodyHtml(item)"
                        ></div>
                        <div
                            class="absolute inset-0 flex items-center justify-center border border-foxy-border/80 bg-foxy-elevated/90 px-3 text-center backdrop-blur-sm"
                        >
                            <div>
                                <div class="text-sm font-semibold text-foxy-text">Публікація містить спойлер</div>
                                <button
                                    type="button"
                                    class="publication-spoiler-reveal mt-2 rounded-lg bg-foxy-action px-3 py-1.5 text-xs font-bold shadow-sm transition-colors hover:bg-foxy-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-surface active:bg-foxy-action"
                                    @click="revealSpoiler(item)"
                                >
                                    Показати спойлер
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else class="mt-3">
                        <div :ref="`preview-${item.id}`" class="publication-preview publication-preview-clamped" v-html="bodyHtml(item)"></div>
                        <a
                            v-if="previewOverflows[String(item.id)]"
                            :href="item.url"
                            class="mt-1.5 inline-flex text-sm font-semibold text-foxy-action transition-colors hover:text-foxy-action/80"
                            >Читати далі</a
                        >
                    </div>

                    <footer class="mt-3 flex flex-wrap items-center gap-1 border-t border-foxy-border/70 pt-2 text-sm">
                        <button
                            v-if="item.capabilities && item.capabilities.can_like"
                            type="button"
                            :class="['publication-action', item.is_liked ? 'is-liked' : '']"
                            :aria-pressed="item.is_liked ? 'true' : 'false'"
                            @click="toggleLike(item)"
                        >
                            <i :class="[item.is_liked ? 'fas' : 'far', 'fa-heart']" aria-hidden="true"></i>
                            <span>{{ item.likes_count || 0 }}</span>
                        </button>

                        <a :href="item.url" class="publication-action" aria-label="Відкрити коментарі">
                            <i class="far fa-comment" aria-hidden="true"></i>
                            <span>{{ item.replies_count || 0 }}</span>
                        </a>

                        <button
                            v-if="item.capabilities && item.capabilities.can_favorite"
                            type="button"
                            class="publication-action"
                            :class="{ 'is-saved': item.is_favorited }"
                            :disabled="favoritePending[String(item.id)]"
                            :aria-label="item.is_favorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                            :aria-pressed="item.is_favorited ? 'true' : 'false'"
                            @click="toggleFavorite(item)"
                        >
                            <i :class="[item.is_favorited ? 'fas' : 'far', 'fa-bookmark']" aria-hidden="true"></i>
                        </button>

                        <button type="button" class="publication-action" aria-label="Поділитися" @click="shareReview(item)">
                            <i class="fas fa-share-nodes" aria-hidden="true"></i>
                        </button>

                        <div v-if="hasOverflow(item)" class="relative ml-auto">
                            <button type="button" class="publication-action" aria-label="Додаткові дії" @click.stop="toggleMenu(item.id)">
                                <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                            </button>
                            <div v-if="activeMenu === item.id" class="publication-overflow-menu">
                                <a v-if="item.capabilities.can_edit" :href="editUrl(item)" class="publication-overflow-item">
                                    <i class="fas fa-edit" aria-hidden="true"></i> Редагувати
                                </a>
                                <button
                                    v-if="item.capabilities.can_delete"
                                    type="button"
                                    class="publication-overflow-item text-red-600 dark:text-red-300"
                                    @click="deleteReview(item)"
                                >
                                    <i class="fas fa-trash" aria-hidden="true"></i> Видалити
                                </button>
                                <button
                                    v-if="item.capabilities.can_report"
                                    type="button"
                                    class="publication-overflow-item text-red-600 dark:text-red-300"
                                    @click="openReportModal(item)"
                                >
                                    <i class="fas fa-flag" aria-hidden="true"></i> Поскаржитися
                                </button>
                            </div>
                        </div>
                    </footer>
                </div>
            </article>

            <p v-if="archiveError" class="publication-state border-red-400/35 text-red-700 dark:text-red-200">
                {{ archiveError }}
            </p>

            <button
                v-if="meta.has_more"
                type="button"
                class="acc-btn mx-auto flex min-w-44 justify-center"
                :disabled="archiveLoading"
                @click="loadMore"
            >
                {{ archiveLoading ? 'Завантаження...' : 'Показати ще' }}
            </button>
        </div>

        <div v-else class="publication-state">
            {{ filtersActive ? 'Публікацій за поточними умовами не знайдено.' : 'Опублікованих рецензій та відгуків поки немає.' }}
        </div>

        <report-modal
            v-if="reportData"
            :show="showReportModal"
            :reportable-type="reportData.type"
            :reportable-id="reportData.id"
            :content-preview="reportData.content"
            :content-url="reportData.url"
            @close="closeReportModal"
        />
    </div>
</template>

<script lang="js">
import axios from 'axios';
import PublicationsNav from '../components/PublicationsNav.vue';
import ReportModal from '../../components/ReportModal.vue';
import { previewHtml, structuredText } from '../../utils/richText';
import { acknowledgeReviewSpoiler, reviewSpoilerAcknowledgedEvent, shouldProtectReviewSpoiler } from '../../utils/reviewSpoiler';

export default {
    name: 'ProfileReviews',
    components: { ReportModal, PublicationsNav },
    props: {
        profile: { type: Object, required: true },
        dashboard: { type: Object, default: null },
        isOwner: { type: Boolean, default: false },
        isRestricted: { type: Boolean, default: false },
        viewer: { type: Object, default: null },
    },
    data() {
        return {
            typeOptions: [
                { value: 'all', label: 'Усі' },
                { value: 'review', label: 'Рецензії' },
                { value: 'opinion', label: 'Відгуки' },
            ],
            searchQuery: '',
            typeFilter: 'all',
            sortOrder: 'newest',
            reviews: [],
            meta: { current_page: 1, last_page: 1, per_page: 20, total: 0, has_more: false },
            archiveLoading: false,
            archiveSearchPending: false,
            archiveError: '',
            requestGeneration: 0,
            searchTimer: null,
            resizeFrame: null,
            previewOverflows: {},
            revealedSpoilerIds: {},
            spoilerAcknowledgedHandler: null,
            favoritePending: {},
            activeMenu: null,
            showReportModal: false,
            reportData: null,
            clickOutsideHandler: null,
        };
    },
    computed: {
        archiveContext() { return JSON.stringify([this.profile?.username, this.searchQuery.trim(), this.typeFilter, this.sortOrder]); },
        filtersActive() {
            return Boolean(this.searchQuery.trim()) || this.typeFilter !== 'all';
        },
        viewerId() {
            return this.viewer?.id === null || this.viewer?.id === undefined ? null : Number(this.viewer.id);
        },
    },
    mounted() {
        this.reloadArchive();
        this.spoilerAcknowledgedHandler = (event) => {
            const reviewId = event.detail?.reviewId;
            if (reviewId !== null && reviewId !== undefined) {
                this.$set(this.revealedSpoilerIds, String(reviewId), true);
                this.$nextTick(this.scheduleMeasurements);
            }
        };
        this.clickOutsideHandler = (event) => {
            if (this.$el && !this.$el.contains(event.target)) this.activeMenu = null;
        };
        window.addEventListener('resize', this.scheduleMeasurements);
        window.addEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
        document.addEventListener('click', this.clickOutsideHandler);
    },
    beforeDestroy() {
        this.requestGeneration += 1;
        if (this.searchTimer) window.clearTimeout(this.searchTimer);
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        window.removeEventListener('resize', this.scheduleMeasurements);
        if (this.spoilerAcknowledgedHandler) {
            window.removeEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
        }
        if (this.clickOutsideHandler) document.removeEventListener('click', this.clickOutsideHandler);
    },
    methods: {
        invalidateArchiveContinuation() {
            this.requestGeneration += 1;
            this.archiveSearchPending = true;
            this.meta = { ...this.meta, has_more: false, current_page: 0 };
        },
        scheduleReload() {
            this.invalidateArchiveContinuation();
            if (this.searchTimer) window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(() => this.reloadArchive(), 300);
        },
        setType(value) {
            if (this.typeFilter === value) return;
            this.typeFilter = value;
            this.reloadArchive();
        },
        reloadArchive() {
            if (this.searchTimer) window.clearTimeout(this.searchTimer);
            this.searchTimer = null;
            this.invalidateArchiveContinuation();
            return this.fetchArchive(1, false);
        },
        loadMore() {
            if (!this.archiveLoading && !this.archiveSearchPending && this.meta.has_more) {
                return this.fetchArchive(Number(this.meta.current_page) + 1, true);
            }
        },
        async fetchArchive(page, append) {
            const username = this.profile?.username;
            if (!username) return;

            const generation = ++this.requestGeneration;
            const context = this.archiveContext;
            this.archiveLoading = true;
            this.archiveError = '';
            try {
                const { data } = await axios.get(`/api/account/profile/${encodeURIComponent(username)}/reviews`, {
                    params: {
                        page,
                        q: this.searchQuery.trim() || undefined,
                        type: this.typeFilter,
                        sort: this.sortOrder,
                    },
                });
                if (generation !== this.requestGeneration || context !== this.archiveContext || this._isDestroyed) return;
                const next = Array.isArray(data?.data) ? data.data : [];
                const merged = append ? [...this.reviews, ...next] : next;
                this.reviews = Array.from(new Map(merged.map(item => [item.id, item])).values());
                this.meta = { ...this.meta, ...(data?.meta || {}) };
                this.$nextTick(this.scheduleMeasurements);
            } catch (error) {
                if (generation !== this.requestGeneration || context !== this.archiveContext || this._isDestroyed) return;
                this.archiveError = error?.response?.data?.message || 'Не вдалося завантажити публікації.';
            } finally {
                if (generation === this.requestGeneration && context === this.archiveContext && !this._isDestroyed) {
                    this.archiveLoading = false;
                    this.archiveSearchPending = false;
                }
            }
        },
        scheduleMeasurements() {
            if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
            this.resizeFrame = window.requestAnimationFrame(() => {
                this.resizeFrame = null;
                this.measurePreviews();
            });
        },
        measurePreviews() {
            const next = {};
            this.reviews.forEach((item) => {
                if (item.status === 'blocked' || this.isSpoilerProtected(item)) return;
                const ref = this.$refs[`preview-${item.id}`];
                const element = Array.isArray(ref) ? ref[0] : ref;
                if (element) next[String(item.id)] = element.scrollHeight > element.clientHeight + 1;
            });
            this.previewOverflows = next;
        },
        bodyHtml(item) {
            return item?.content ? previewHtml(item.content, null, { allowImages: false }) : '';
        },
        typeLabel(type) {
            return type === 'opinion' ? 'Відгук' : 'Рецензія';
        },
        formatLabel(item) {
            return Array.isArray(item?.format_labels) ? item.format_labels.filter(Boolean).join(' · ') : '';
        },
        formatDate(value) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '';
            return date.toLocaleDateString('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
            });
        },
        moodPresentation(mood) {
            const moods = {
                positive: { label: 'Позитивні враження', classes: 'is-positive', dotClass: 'bg-foxy-success' },
                mixed: { label: 'Змішані враження', classes: 'is-mixed', dotClass: 'bg-yellow-500' },
                negative: { label: 'Негативні враження', classes: 'is-negative', dotClass: 'bg-foxy-error' },
            };
            return moods[mood] || null;
        },
        isSpoilerProtected(item) {
            return shouldProtectReviewSpoiler(item, this.viewerId, Boolean(this.revealedSpoilerIds[String(item.id)]));
        },
        revealSpoiler(item) {
            acknowledgeReviewSpoiler(item.id);
            this.$set(this.revealedSpoilerIds, String(item.id), true);
            this.$nextTick(this.scheduleMeasurements);
        },
        resolveBookCover(cover) {
            return cover || '/images/placeholders/book-cover.svg';
        },
        onCoverError(event) {
            const fallback = '/images/placeholders/book-cover.svg';
            if (event?.target && !String(event.target.src || '').includes(fallback)) event.target.src = fallback;
        },
        detailUrl(item) {
            return item?.url || (item?.book?.slug && item?.id ? `/books/${item.book.slug}/reviews/${item.id}` : '#');
        },
        editUrl(item) {
            return item?.book?.slug && item?.id ? `/books/${item.book.slug}/reviews/${item.id}/edit` : '#';
        },
        hasOverflow(item) {
            const capabilities = item?.capabilities || {};
            return Boolean(capabilities.can_edit || capabilities.can_delete || capabilities.can_report);
        },
        toggleMenu(id) {
            this.activeMenu = this.activeMenu === id ? null : id;
        },
        async toggleLike(item) {
            if (!item?.capabilities?.can_like || !item?.book?.slug) return;
            try {
                const { data } = await axios.post(`/books/${item.book.slug}/reviews/${item.id}/like`);
                if (data?.success) {
                    this.$set(item, 'is_liked', Boolean(data.is_liked));
                    this.$set(item, 'likes_count', Number(data.likes_count || 0));
                }
            } catch (error) {
                this.archiveError = error?.response?.data?.message || 'Не вдалося оновити вподобання.';
            }
        },
        async toggleFavorite(item) {
            const key = String(item.id);
            if (!item?.capabilities?.can_favorite || this.favoritePending[key] || !item?.book?.slug) return;
            this.$set(this.favoritePending, key, true);
            try {
                const { data } = await axios.post(`/books/${item.book.slug}/reviews/${item.id}/favorite`);
                if (data?.success) this.$set(item, 'is_favorited', Boolean(data.is_favorited));
            } catch (error) {
                this.archiveError = error?.response?.data?.message || 'Не вдалося оновити збережене.';
            } finally {
                this.$delete(this.favoritePending, key);
            }
        },
        async shareReview(item) {
            const text = structuredText(item?.content || '');
            const { shareContent } = await import('../../utils/shareHelper');
            await shareContent({
                title: item?.book?.title ? `Публікація про «${item.book.title}»` : 'Публікація FOXY',
                text: text.length > 200 ? `${text.slice(0, 200).trim()}…` : text,
                url: new URL(this.detailUrl(item), window.location.origin).href,
            });
        },
        openReportModal(item) {
            const text = structuredText(item?.content || '');
            this.reportData = {
                type: 'App\\Models\\Review',
                id: item.id,
                content: text.length > 200 ? `${text.slice(0, 200).trim()}…` : text,
                url: new URL(this.detailUrl(item), window.location.origin).href,
            };
            this.showReportModal = true;
            this.activeMenu = null;
        },
        closeReportModal() {
            this.showReportModal = false;
            this.reportData = null;
        },
        async deleteReview(item) {
            this.activeMenu = null;
            const confirmed = await window.confirm('Ви впевнені, що хочете видалити цю публікацію?');
            if (!confirmed || !item?.book?.slug) return;
            const username = this.profile.username;
            try {
                const { data } = await axios.delete(`/books/${item.book.slug}/reviews/${item.id}`);
                if (data?.success === true && !this._isDestroyed && username === this.profile.username) window.location.reload();
            } catch (error) {
                this.archiveError = error?.response?.data?.message || 'Не вдалося видалити публікацію.';
            }
        },
    },
};
</script>

<style scoped>
.publication-card {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    border: 1px solid rgb(var(--foxy-border) / 0.82);
    border-radius: 1rem;
    background: rgb(var(--foxy-surface) / 0.82);
    padding: 1rem;
    box-shadow: 0 1px 3px rgb(51 35 24 / 0.04);
    transition:
        border-color 160ms ease,
        box-shadow 160ms ease;
}

.publication-card:hover {
    border-color: color-mix(in srgb, var(--acc-accent) 32%, rgb(var(--foxy-border)));
    box-shadow: 0 8px 28px rgb(51 35 24 / 0.07);
}

.publication-mood {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border: 1px solid;
    border-radius: 9999px;
    padding: 0.25rem 0.55rem;
    font-size: 0.7rem;
    font-weight: 650;
}

.publication-mood.is-positive {
    border-color: rgb(var(--foxy-success) / 0.5);
    background: rgb(var(--foxy-success) / 0.1);
}
.publication-mood.is-mixed {
    border-color: rgb(202 138 4 / 0.45);
    background: rgb(254 249 195 / 0.72);
    color: rgb(113 63 18);
}
.publication-mood.is-negative {
    border-color: rgb(var(--foxy-error) / 0.48);
    background: rgb(var(--foxy-error) / 0.09);
}
html.dark .publication-mood.is-mixed {
    background: rgb(234 179 8 / 0.12);
    color: rgb(254 240 138);
}

.publication-preview {
    color: rgb(var(--foxy-text));
    font-size: 0.94rem;
    line-height: 1.65rem;
    overflow-wrap: anywhere;
}

.publication-preview-clamped {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    max-height: 6.6rem;
}

.publication-preview ::v-deep p,
.publication-preview ::v-deep blockquote,
.publication-preview ::v-deep ul,
.publication-preview ::v-deep ol {
    margin: 0;
}

.publication-preview ::v-deep p + p {
    margin-top: 0.35rem;
}

.publication-spoiler-reveal {
    color: rgb(var(--foxy-action-foreground));
}

.publication-action {
    display: inline-flex;
    min-height: 2.25rem;
    align-items: center;
    gap: 0.4rem;
    border-radius: 0.6rem;
    padding: 0.35rem 0.6rem;
    color: rgb(var(--foxy-text-secondary));
    font-weight: 600;
    transition: 150ms ease;
}

.publication-action:hover {
    background: rgb(var(--foxy-elevated));
    color: rgb(var(--foxy-action));
}
.publication-action.is-liked {
    background: rgb(var(--foxy-error) / 0.08);
    color: rgb(var(--foxy-error));
}
.publication-action.is-saved {
    background: rgb(var(--foxy-honey) / 0.1);
    color: rgb(var(--foxy-text));
}
.publication-action:disabled {
    cursor: wait;
    opacity: 0.55;
}

.publication-overflow-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 0.35rem);
    z-index: 30;
    min-width: 11rem;
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.75rem;
    background: rgb(var(--foxy-surface));
    padding: 0.35rem;
    box-shadow: 0 16px 35px rgb(51 35 24 / 0.18);
}

.publication-overflow-item {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.55rem;
    border-radius: 0.5rem;
    padding: 0.55rem 0.7rem;
    text-align: left;
    font-size: 0.82rem;
    font-weight: 600;
}

.publication-overflow-item:hover {
    background: rgb(var(--foxy-elevated));
}

.publication-state {
    border: 1px solid rgb(var(--foxy-border) / 0.8);
    border-radius: 1rem;
    background: rgb(var(--foxy-surface) / 0.72);
    padding: 1rem;
    color: rgb(var(--foxy-text-secondary));
    font-size: 0.875rem;
    text-align: center;
}

@media (max-width: 420px) {
    .publication-card {
        gap: 0.7rem;
        padding: 0.8rem;
    }
    .publication-mood {
        font-size: 0.65rem;
    }
}
</style>
